// dashboard/views/SupportView.js
// Admin-only. Extracted from App.js (2026-08-12 split).

import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import { c, onActivateKeyDown } from '../constants';

const PAGE_SIZE = 25;
const SUPPORT_VIEWS = {
  actionable: ['new', 'classified', 'ai_drafted', 'waiting_human', 'failed'],
  waiting: ['waiting_user', 'auto_replied'],
  resolved: ['resolved', 'closed'],
};

export default function SupportView({ profile }) {
  const { t } = useTranslation();
  const [tickets,      setTickets]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [fetchError,   setFetchError]   = useState('');
  const [queueView,    setQueueView]    = useState('actionable');
  const [searchDraft,  setSearchDraft]  = useState('');
  const [searchTerm,   setSearchTerm]   = useState('');
  const [page,         setPage]         = useState(0);
  const [hasMore,      setHasMore]      = useState(false);
  const [totalCount,   setTotalCount]   = useState(0);
  const [selected,     setSelected]     = useState(null);   // row from list
  const [detail,       setDetail]       = useState(null);   // full ticket + messages
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText,    setReplyText]    = useState('');
  const [sending,      setSending]      = useState(false);
  const [sendError,    setSendError]    = useState('');
  const [sendSuccess,  setSendSuccess]  = useState(false);
  // Set when postmark-send-reply's own duplicate-send guard column
  // failed to persist even after its retries -- the email was already
  // sent (irreversible) but the ticket doesn't know it. Reloading the
  // ticket here would show it as still not-sent, inviting exactly the
  // duplicate real email this state exists to prevent -- see handleSend.
  const [trackingFailedTicketId, setTrackingFailedTicketId] = useState(null);

  const statusColor = s => ({
    new: c.sandstone, classified: c.sandstone, ai_drafted: c.yellow,
    waiting_human: c.yellow, waiting_user: c.moss, auto_replied: c.moss,
    resolved: c.moss, closed: c.textSoft, failed: c.error,
  }[s] || c.textSoft);

  const statusBg = s => ({
    new: c.sandstoneLight, classified: c.sandstoneLight, ai_drafted: c.yellowLight,
    waiting_human: c.yellowLight, waiting_user: c.greenLight, auto_replied: c.greenLight,
    resolved: c.greenLight, closed: c.fog, failed: '#fff5f5',
  }[s] || c.fog);

  async function loadTickets({ append = false } = {}) {
    setLoading(true);
    setFetchError('');
    try {
      const targetPage = append ? page + 1 : 0;
      const from = targetPage * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      let query = supabase
        .from('support_tickets')
        .select('id, subject, status, priority, category, created_at, sender_email, ai_safety_flag', { count: 'exact' });
      if (SUPPORT_VIEWS[queueView]) query = query.in('status', SUPPORT_VIEWS[queueView]);
      if (searchTerm) {
        const searchColumn = searchTerm.includes('@') ? 'sender_email' : 'subject';
        query = query.ilike(searchColumn, `%${searchTerm}%`);
      }
      // Actionable work is oldest-first, with deterministic safety-flagged
      // cases ahead of the rest. Pagination means no unresolved item falls
      // permanently outside a fixed newest-N window.
      query = query
        .order('ai_safety_flag', { ascending: false })
        .order('created_at', { ascending: queueView === 'actionable' })
        .range(from, to);
      const { data, error, count } = await query;
      if (error) {
        if (error.code === '42P01') setTickets([]);
        else setFetchError(t('supportView.loadTicketsError'));
      } else {
        const rows = data || [];
        setTickets(previous => append ? [...previous, ...rows] : rows);
        setPage(targetPage);
        setTotalCount(count || 0);
        setHasMore(from + rows.length < (count || 0));
      }
    } catch (_e) {
      setFetchError(t('supportView.loadTicketsError'));
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(ticketId) {
    setDetailLoading(true);
    setDetail(null);
    setSendError('');
    setSendSuccess(false);
    try {
      const [{ data: ticket }, { data: messages }] = await Promise.all([
        supabase
          .from('support_tickets')
          .select('*, profiles!reviewed_by(first_name, last_name)')
          .eq('id', ticketId)
          .single(),
        supabase
          .from('support_messages')
          .select('id, direction, body, sender_email, created_at')
          .eq('ticket_id', ticketId)
          .order('created_at', { ascending: true }),
      ]);
      setDetail({ ticket, messages: messages || [] });
      setReplyText(ticket?.ai_suggested_response || '');
    } catch (_e) {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    setSelected(null);
    setDetail(null);
    loadTickets();
  }, [queueView, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  function selectTicket(row) {
    if (selected?.id === row.id) {
      setSelected(null);
      setDetail(null);
      return;
    }
    setSelected(row);
    loadDetail(row.id);
  }

  async function handleSend() {
    if (!detail?.ticket || !replyText.trim() || sending) return;
    setSending(true);
    setSendError('');
    setSendSuccess(false);
    try {
      // Guards against a stalled connection leaving this hung forever with
      // no feedback and no way to retry short of a full page reload — a
      // real incident (2026-08-05): the reply eventually sent on a second
      // attempt after reload, but the first attempt never even reached the
      // edge function according to its own logs, meaning this was a
      // client/network-layer stall, not a slow server, and nothing here
      // ever timed out to surface it. Safe to let the admin retry after
      // this fires even if the first attempt secretly did succeed —
      // postmark-send-reply's own outbound_provider_message_id guard
      // rejects a genuine duplicate with a 409 instead of sending twice.
      const SEND_TIMEOUT_MS = 20000;
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error(t('supportView.sendTimedOut'))), SEND_TIMEOUT_MS)
      );
      const { data, error } = await Promise.race([
        supabase.functions.invoke('postmark-send-reply', {
          body: { ticket_id: detail.ticket.id, reply_body: replyText.trim() },
        }),
        timeout,
      ]);
      if (error || !data?.ok) {
        setSendError(data?.error || error?.message || t('supportView.sendFailed'));
        return;
      }
      if (data?.trackingFailed) {
        // The email genuinely sent -- do NOT reload into a state that
        // looks like it's still safe to send, and do not show the normal
        // success indicator either. This needs a human to manually
        // confirm the ticket's real state before anyone touches Send
        // again.
        setTrackingFailedTicketId(detail.ticket.id);
        return;
      }
      setSendSuccess(true);
      // Refresh ticket list + detail
      await loadTickets();
      await loadDetail(detail.ticket.id);
    } catch (err) {
      setSendError(err.message || t('supportView.unexpectedError'));
    } finally {
      setSending(false);
    }
  }

  async function handleResolve() {
    if (!detail?.ticket) return;
    setSendError('');
    const { error } = await supabase.from('support_tickets')
      .update({ status: 'resolved', resolved_at: new Date().toISOString() })
      .eq('id', detail.ticket.id);
    if (error) {
      setSendError(t('supportView.resolveFailed'));
      return;
    }
    setSelected(null);
    setDetail(null);
    await loadTickets();
  }

  const alreadySent = !!(detail?.ticket?.outbound_provider_message_id);
  const trackingFailedForThisTicket = !!detail?.ticket && trackingFailedTicketId === detail.ticket.id;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: c.slate, margin: '0 0 4px' }}>{t('nav.support')}</h1>
          <p style={{ fontSize: 13, color: c.textSoft, margin: 0 }}>{t('supportView.subtitle')}</p>
        </div>
        <button onClick={() => loadTickets()} style={{ padding: '7px 12px', border: `1px solid ${c.border}`, borderRadius: 8, background: 'none', fontSize: 12, color: c.textSoft, cursor: 'pointer' }}>{t('supportView.refresh')}</button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 16 }} aria-label={t('supportView.queueViews')}>
        {['actionable', 'waiting', 'resolved', 'all'].map(option => (
          <button
            key={option}
            type="button"
            onClick={() => setQueueView(option)}
            aria-pressed={queueView === option}
            style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${queueView === option ? c.moss : c.border}`, backgroundColor: queueView === option ? c.greenLight : c.white, color: queueView === option ? c.moss : c.textSoft, fontSize: 12, fontWeight: queueView === option ? 600 : 400, cursor: 'pointer' }}
          >
            {t(`supportView.view_${option}`)}
          </button>
        ))}
        {!loading && !fetchError && (
          <span style={{ marginLeft: 'auto', color: c.textSoft, fontSize: 12 }}>
            {t('supportView.ticketCount', { count: totalCount })}
          </span>
        )}
      </div>

      <form
        role="search"
        onSubmit={event => {
          event.preventDefault();
          setSearchTerm(searchDraft.trim().slice(0, 100));
        }}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}
      >
        <label htmlFor="support-search" style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>
          {t('supportView.searchLabel')}
        </label>
        <input
          id="support-search"
          type="search"
          value={searchDraft}
          onChange={event => setSearchDraft(event.target.value)}
          placeholder={t('supportView.searchPlaceholder')}
          maxLength={100}
          style={{ flex: '1 1 240px', minWidth: 0, padding: '8px 10px', border: `1px solid ${c.border}`, borderRadius: 8, color: c.slate, backgroundColor: c.white }}
        />
        <button type="submit" style={{ padding: '8px 14px', border: `1px solid ${c.moss}`, borderRadius: 8, backgroundColor: c.moss, color: c.white, cursor: 'pointer' }}>
          {t('supportView.search')}
        </button>
        {searchTerm && (
          <button
            type="button"
            onClick={() => { setSearchDraft(''); setSearchTerm(''); }}
            style={{ padding: '8px 14px', border: `1px solid ${c.border}`, borderRadius: 8, backgroundColor: c.white, color: c.textSoft, cursor: 'pointer' }}
          >
            {t('supportView.clearSearch')}
          </button>
        )}
      </form>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: c.textSoft }}>{t('supportView.loadingTickets')}</div>
      ) : fetchError ? (
        <div style={{ backgroundColor: '#fff5f5', borderRadius: 12, padding: 48, textAlign: 'center', color: c.error, border: `1px solid ${c.error}33` }}>{fetchError}</div>
      ) : tickets.length === 0 ? (
        <div style={{ backgroundColor: c.white, borderRadius: 12, padding: 48, textAlign: 'center', color: c.textSoft, border: `1px solid ${c.border}` }}>
          <p style={{ fontSize: 15, fontWeight: 500, color: c.slate, marginBottom: 8 }}>{t('supportView.emptyTitle')}</p>
          <p style={{ fontSize: 13, margin: 0 }}>{t('supportView.emptyBody')}</p>
        </div>
      ) : (
        <div className="support-layout" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          {/* Ticket list */}
          <div className="support-ticket-list" style={{ flex: 1, minWidth: 0, backgroundColor: c.white, borderRadius: 12, border: `1px solid ${c.border}`, overflow: 'hidden' }}>
            <div className="support-table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: c.fog }}>
                  {[t('supportView.colSubject'), t('supportView.colFrom'), t('supportView.colCategory'), t('supportView.colStatus'), t('supportView.colReceived')].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: c.textSoft, borderBottom: `1px solid ${c.border}`, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tickets.map(row => (
                  <tr
                    key={row.id}
                    onClick={() => selectTicket(row)}
                    onKeyDown={onActivateKeyDown(() => selectTicket(row))}
                    tabIndex={0}
                    role="button"
                    aria-label={t('supportView.ticketA11y', { subject: row.subject || t('supportView.noSubject'), status: (row.status || 'new').replace(/_/g, ' ') })}
                    style={{ cursor: 'pointer', backgroundColor: selected?.id === row.id ? c.fog : 'transparent' }}
                  >
                    <td style={{ padding: '12px 16px', fontSize: 13, color: c.slate, borderBottom: `1px solid ${c.border}`, maxWidth: 220 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {row.ai_safety_flag && <span title={t('supportView.medicalFlagTitle')} style={{ color: c.error, fontSize: 11 }}>⚠</span>}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.subject || t('supportView.noSubject')}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: c.textSoft, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' }}>
                      {row.sender_email || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: c.textSoft, borderBottom: `1px solid ${c.border}` }}>
                      {row.category ? row.category.replace(/_/g, ' ') : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: `1px solid ${c.border}` }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: statusColor(row.status), backgroundColor: statusBg(row.status), padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                        {(row.status || 'new').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: c.textSoft, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' }}>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {hasMore && (
              <div style={{ padding: 12, textAlign: 'center', borderTop: `1px solid ${c.border}` }}>
                <button
                  type="button"
                  onClick={() => loadTickets({ append: true })}
                  disabled={loading}
                  style={{ padding: '8px 16px', border: `1px solid ${c.border}`, borderRadius: 8, backgroundColor: c.white, color: c.slate, fontSize: 12, cursor: loading ? 'default' : 'pointer' }}
                >
                  {loading ? t('common.loading') : t('supportView.loadMore')}
                </button>
              </div>
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <div className="support-detail-panel" style={{ width: 420, flexShrink: 0, backgroundColor: c.white, borderRadius: 12, border: `1px solid ${c.border}`, padding: 24, maxHeight: '80vh', overflowY: 'auto' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                  <div style={{ fontSize: 11, color: c.textSoft, fontFamily: 'monospace', marginBottom: 4 }}>{selected.id.slice(0, 8)}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: c.slate, marginBottom: 2 }}>{selected.subject || t('supportView.noSubject')}</div>
                  <div style={{ fontSize: 12, color: c.textSoft }}>{selected.sender_email}</div>
                </div>
                <button onClick={() => { setSelected(null); setDetail(null); }} aria-label={t('supportView.closeTicketA11y')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textSoft, fontSize: 18, lineHeight: 1, padding: 4, flexShrink: 0 }}>×</button>
              </div>

              {detailLoading ? (
                <div style={{ textAlign: 'center', padding: 32, color: c.textSoft, fontSize: 13 }}>{t('common.loading')}</div>
              ) : detail ? (
                <>
                  {/* Metadata chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                    {detail.ticket.category && (
                      <span style={{ fontSize: 11, backgroundColor: c.fog, color: c.slate, padding: '2px 8px', borderRadius: 10 }}>
                        {detail.ticket.category.replace(/_/g, ' ')}
                      </span>
                    )}
                    {detail.ticket.priority && detail.ticket.priority !== 'normal' && (
                      <span style={{ fontSize: 11, backgroundColor: detail.ticket.priority === 'urgent' ? '#fff5f5' : c.yellowLight, color: detail.ticket.priority === 'urgent' ? c.error : c.sandstoneText, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                        {detail.ticket.priority}
                      </span>
                    )}
                    {detail.ticket.ai_safety_flag && (
                      <span style={{ fontSize: 11, backgroundColor: '#fff5f5', color: c.error, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{t('supportView.medicalFlagBadge')}</span>
                    )}
                    {detail.ticket.confidence != null && (
                      <span style={{ fontSize: 11, backgroundColor: c.fog, color: c.textSoft, padding: '2px 8px', borderRadius: 10 }}>
                        {t('supportView.confidencePercent', { percent: Math.round(detail.ticket.confidence * 100) })}
                      </span>
                    )}
                  </div>

                  {/* AI summary */}
                  {detail.ticket.ai_summary && (
                    <div style={{ marginBottom: 16, padding: 12, backgroundColor: c.fog, borderRadius: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{t('supportView.aiSummary')}</div>
                      <div style={{ fontSize: 13, color: c.slate, lineHeight: 1.5 }}>{detail.ticket.ai_summary}</div>
                    </div>
                  )}

                  {/* Message thread */}
                  {detail.messages.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('supportView.thread')}</div>
                      {detail.messages.map(msg => (
                        <div key={msg.id} style={{
                          marginBottom: 10,
                          padding: 12,
                          borderRadius: 8,
                          backgroundColor: msg.direction === 'outbound' ? c.greenLight : c.fog,
                          borderLeft: `3px solid ${msg.direction === 'outbound' ? c.moss : c.border}`,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: msg.direction === 'outbound' ? c.moss : c.slate }}>
                              {msg.direction === 'outbound' ? t('supportView.cairnSupport') : msg.sender_email}
                            </span>
                            <span style={{ fontSize: 11, color: c.textSoft }}>
                              {new Date(msg.created_at).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </span>
                          </div>
                          <div style={{ fontSize: 13, color: c.slate, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{msg.body}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Send succeeded at Postmark but the DB write that
                      records it (this function's own duplicate-send
                      guard) failed even after retries -- do not show the
                      reply editor at all, since re-enabling it invites a
                      second real email to the customer. */}
                  {trackingFailedForThisTicket && (
                    <div role="alert" style={{ padding: 12, backgroundColor: '#fff5f5', border: `1px solid ${c.error}33`, borderRadius: 8, fontSize: 13, color: c.error, fontWeight: 500 }}>
                      {t('supportView.trackingFailedWarning')}
                    </div>
                  )}

                  {/* Reply editor */}
                  {!trackingFailedForThisTicket && !alreadySent && ['new', 'classified', 'ai_drafted', 'waiting_human', 'failed'].includes(detail.ticket.status) && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                        {detail.ticket.ai_suggested_response ? t('supportView.aiDraftLabel') : t('supportView.writeReply')}
                      </div>
                      <textarea
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        rows={7}
                        aria-label={t('supportView.replyA11y')}
                        style={{
                          width: '100%', boxSizing: 'border-box',
                          padding: 12, borderRadius: 8, border: `1px solid ${c.border}`,
                          fontSize: 13, color: c.slate, lineHeight: 1.6,
                          fontFamily: 'Inter, system-ui, sans-serif',
                          resize: 'vertical', outline: 'none',
                          backgroundColor: sendSuccess ? c.greenLight : c.white,
                        }}
                        placeholder={t('supportView.replyPlaceholder')}
                        disabled={sending || sendSuccess}
                      />
                      {sendError && (
                        <div role="alert" style={{ fontSize: 12, color: c.error, marginTop: 6 }}>{sendError}</div>
                      )}
                      {sendSuccess && (
                        <div style={{ fontSize: 12, color: c.moss, marginTop: 6, fontWeight: 500 }}>{t('supportView.replySentSuccess')}</div>
                      )}
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button
                          onClick={handleSend}
                          disabled={!replyText.trim() || sending || sendSuccess}
                          style={{
                            flex: 1, padding: '9px 16px', backgroundColor: (!replyText.trim() || sending || sendSuccess) ? c.border : c.moss,
                            color: c.white, border: 'none', borderRadius: 8,
                            fontSize: 13, fontWeight: 600, cursor: (!replyText.trim() || sending || sendSuccess) ? 'default' : 'pointer',
                          }}
                        >
                          {sending ? t('supportView.sending') : t('supportView.approveAndSend')}
                        </button>
                        <button
                          onClick={handleResolve}
                          style={{ padding: '9px 14px', background: 'none', border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, color: c.textSoft, cursor: 'pointer' }}
                        >
                          {t('supportView.resolve')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Already sent — show status */}
                  {alreadySent && (
                    <div style={{ padding: 12, backgroundColor: c.greenLight, borderRadius: 8, fontSize: 13, color: c.moss, fontWeight: 500 }}>
                      {t('supportView.alreadySent')}
                    </div>
                  )}

                  {detail.ticket.status === 'resolved' && (
                    <div style={{ padding: 12, backgroundColor: c.fog, borderRadius: 8, fontSize: 13, color: c.textSoft }}>
                      {detail.ticket.resolved_at
                        ? t('supportView.resolvedWithDate', { date: new Date(detail.ticket.resolved_at).toLocaleDateString(i18n.language) })
                        : t('supportView.resolvedNoDate')}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 13, color: c.textSoft }}>{t('supportView.couldNotLoadDetails')}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
